import React from "react";
import type { Fact, RequestDetail } from "@/api";

/**
 * The first rendered mark that carries the fact. A stretch of text can belong to several facts,
 * so the fact is looked up in the mark's whole list, not in the one it shows at rest (#2128).
 */
export function findEvidenceMark(root: ParentNode | null | undefined, fieldPath: string, within = ""): Element | null {
  return Array.from(root?.querySelectorAll(`${within}[data-marks]`) ?? [])
    .find((mark) => mark.getAttribute("data-marks")?.split("\n").includes(fieldPath)) ?? null;
}

export function useEvidenceSelection(messages: RequestDetail["messages"]) {
  const [selectedMessageKey, setSelectedMessageKey] = React.useState<string | null>(null);
  const [hoverMark, setHoverMark] = React.useState<string | null>(null);
  const [pinnedMark, setPinnedMark] = React.useState<string | null>(null);
  const activeMark = hoverMark ?? pinnedMark;

  const selectMessage = (key: string) => {
    setSelectedMessageKey(key);
    setHoverMark(null);
  };
  const selectFactEvidence = (fact: Fact) => {
    const sourceMessageId = fact.source_message_id ?? null;
    if (sourceMessageId && messages.some((message) => message.id === sourceMessageId)) {
      setSelectedMessageKey(sourceMessageId);
    }
    setPinnedMark(fact.fieldPath);
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const element = findEvidenceMark(document, fact.fieldPath, "[data-message-key] ")
          ?? findEvidenceMark(document, fact.fieldPath);
        element?.scrollIntoView({
          behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
          block: "center",
        });
      });
    });
  };

  return {
    selectedMessageKey,
    activeMark,
    setSelectedMessageKey,
    setHoverMark,
    setPinnedMark,
    selectMessage,
    selectFactEvidence,
  };
}
